/**
 * envelopes_per_month === 0 means unlimited → limit: null
 */
export function normalizeMonthlyLimit(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
}

export function normalizeUsageEntry(used, limitRaw, remainingRaw, canSendRaw) {
  const usedNum = Number(used);
  const usedSafe = Number.isFinite(usedNum) ? usedNum : 0;
  const limit = normalizeMonthlyLimit(limitRaw);
  const remainingNum = Number(remainingRaw);
  const remaining =
    Number.isFinite(remainingNum)
      ? Math.max(0, remainingNum)
      : limit != null
        ? Math.max(0, limit - usedSafe)
        : null;
  const entry = {
    used: usedSafe,
    limit,
    remaining,
  };
  if (canSendRaw !== undefined && canSendRaw !== null) {
    entry.canSend = canSendRaw === true || canSendRaw === 1 || canSendRaw === '1';
  }
  return entry;
}

function normalizeEnvelopesUsageEntry(rawUsage, subscriptionRaw = {}) {
  const plan =
    subscriptionRaw.plan && typeof subscriptionRaw.plan === 'object'
      ? subscriptionRaw.plan
      : {};
  const planLimit = plan.envelopes_per_month ?? plan.envelopes?.per_month;

  const monthly = rawUsage.envelopes_sent_per_month;
  if (monthly && typeof monthly === 'object') {
    return normalizeUsageEntry(
      monthly.used,
      monthly.limit ?? planLimit,
      monthly.remaining,
      monthly.can_send
    );
  }

  if (rawUsage.envelopes && typeof rawUsage.envelopes === 'object') {
    return normalizeUsageEntry(
      rawUsage.envelopes.used ?? rawUsage.envelopes.count ?? rawUsage.envelopes_sent,
      rawUsage.envelopes.limit
        ?? rawUsage.envelopes.limit_per_month
        ?? rawUsage.envelopes_per_month
        ?? planLimit,
      rawUsage.envelopes.remaining
    );
  }

  return normalizeUsageEntry(
    rawUsage.envelopes_sent ?? rawUsage.envelopes_used ?? rawUsage.used ?? 0,
    rawUsage.envelopes_per_month ?? rawUsage.envelope_limit ?? planLimit
  );
}

function normalizeUsersUsageEntry(rawUsage, subscriptionRaw = {}) {
  if (typeof rawUsage?.users === 'number') {
    return normalizeUsageEntry(
      rawUsage.users,
      subscriptionRaw.users_limit
        ?? subscriptionRaw.max_users
        ?? subscriptionRaw.users_per_month
    );
  }

  const plan =
    subscriptionRaw.plan && typeof subscriptionRaw.plan === 'object'
      ? subscriptionRaw.plan
      : {};
  const planUserLimit = plan.users_allowed ?? plan.users_per_month ?? plan.max_users;

  if (rawUsage?.users && typeof rawUsage.users === 'object') {
    return normalizeUsageEntry(
      rawUsage.users.used ?? rawUsage.users.count ?? rawUsage.users.active,
      rawUsage.users.limit
        ?? rawUsage.users.limit_per_month
        ?? rawUsage.users.max
        ?? rawUsage.users_per_month
        ?? planUserLimit,
      rawUsage.users.remaining
    );
  }

  const used =
    subscriptionRaw.user_count
    ?? subscriptionRaw.users_count
    ?? subscriptionRaw.active_users
    ?? rawUsage?.user_count
    ?? rawUsage?.users_count;
  const limit =
    subscriptionRaw.users_limit
    ?? subscriptionRaw.max_users
    ?? subscriptionRaw.users_per_month
    ?? planUserLimit
    ?? rawUsage?.users_limit
    ?? rawUsage?.max_users
    ?? rawUsage?.users_per_month;

  if (used == null && limit == null) return null;
  return normalizeUsageEntry(used ?? 0, limit);
}

export function normalizeUsage(rawUsage, subscriptionRaw = {}) {
  const usage = rawUsage && typeof rawUsage === 'object' ? rawUsage : {};
  const root = subscriptionRaw && typeof subscriptionRaw === 'object' ? subscriptionRaw : {};

  const envelopes = normalizeEnvelopesUsageEntry(usage, root);
  const users = normalizeUsersUsageEntry(usage, root);
  const result = { envelopes };
  if (users) result.users = users;
  return result;
}

const USAGE_RESOURCE_LABELS = {
  envelopes: { unit: 'envelope', units: 'envelopes', suffix: ' sent this period' },
  users: { unit: 'user', units: 'users', suffix: '' },
};

export function normalizePlanLimits(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const envelopesPerMonth =
    raw.envelopes_per_month ??
    raw.envelopes?.per_month ??
    raw.envelopes?.limit;
  const usersAllowed =
    raw.users_allowed ?? raw.users_per_month ?? raw.max_users;
  return {
    envelopesPerMonth: normalizeMonthlyLimit(envelopesPerMonth),
    usersAllowed: normalizeMonthlyLimit(usersAllowed),
  };
}

/**
 * @param {object|null} raw From GET /user subscription or GET /organization/subscription data
 */
export function extractEnvelopeQuotaFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const candidates = [
    payload.envelope_quota,
    payload.data?.envelope_quota,
    payload.data?.data?.envelope_quota,
  ];
  for (const candidate of candidates) {
    if (
      candidate
      && typeof candidate === 'object'
      && (
        candidate.envelopes_sent_per_month
        || candidate.pay_per_envelope
        || candidate.envelope_credits
        || candidate.can_send !== undefined
        || candidate.bypass !== undefined
      )
    ) {
      return candidate;
    }
  }
  return null;
}

/** Lifetime prepaid envelope credits remaining on the org. */
export function normalizeEnvelopeCredits(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? Math.max(0, raw) : null;
  }
  if (typeof raw === 'object') {
    const remaining = Number(
      raw.remaining ?? raw.balance ?? raw.count ?? raw.credits
    );
    if (Number.isFinite(remaining)) return Math.max(0, remaining);
  }
  return null;
}

export function getEnvelopeCreditsRemaining(subscription) {
  if (!subscription) return null;
  if (subscription.envelopeCredits != null) {
    return normalizeEnvelopeCredits(subscription.envelopeCredits);
  }
  const fromQuota = subscription.envelopeQuota?.raw?.envelope_credits
    ?? subscription.raw?.envelope_quota?.envelope_credits
    ?? subscription.raw?.envelope_credits;
  return normalizeEnvelopeCredits(fromQuota);
}

export function formatEnvelopeCreditsLabel(credits) {
  if (credits == null) return null;
  const n = Number(credits);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n} prepaid credit${n === 1 ? '' : 's'}`;
}

export function normalizePayPerEnvelope(raw) {
  const ppe = raw?.pay_per_envelope;
  if (!ppe || typeof ppe !== 'object') return null;
  return {
    enabled: ppe.enabled === true,
    amountCents: Number(ppe.amount_cents) || 99,
    currency: String(ppe.currency || 'usd').toLowerCase(),
    requiresPayment: ppe.requires_payment === true,
  };
}

export function normalizeEnvelopeQuotaMeta(raw) {
  if (!raw || typeof raw !== 'object') {
    return { metered: false, bypass: false, canSend: null, payPerEnvelope: null };
  }
  const monthly = raw.envelopes_sent_per_month;
  const canSend =
    monthly?.can_send !== undefined
      ? monthly.can_send === true || monthly.can_send === 1 || monthly.can_send === '1'
      : null;
  return {
    metered: raw.metered === true,
    bypass: raw.bypass === true,
    canSend,
    payPerEnvelope: normalizePayPerEnvelope(raw),
  };
}

/**
 * Hide usage banner / header remaining when org is non-metered or user bypasses quota.
 * Contract: hide when `metered === false` or `bypass === true`.
 */
export function shouldShowSubscriptionUsage(subscription) {
  if (!subscription) return false;
  if (subscription.bypass === true || subscription.envelopeQuota?.bypass === true) {
    return false;
  }
  const metered = subscription.metered ?? subscription.envelopeQuota?.metered;
  if (metered === false) return false;
  return Boolean(subscription.usage?.envelopes);
}

export function getPayPerEnvelopeOptions(subscription) {
  if (!subscription) return null;
  return (
    subscription.envelopeQuota?.payPerEnvelope
    ?? normalizePayPerEnvelope(subscription.raw?.envelope_quota)
    ?? null
  );
}

export function isPayPerEnvelopeEnabled(subscription) {
  const opts = getPayPerEnvelopeOptions(subscription);
  return opts?.enabled === true;
}

export function formatPayPerEnvelopePrice(amountCents = 99, currency = 'usd') {
  const code = String(currency || 'usd').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
    }).format(amountCents / 100);
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
}

/** Build subscription-shaped state from API envelope_quota block. */
export function subscriptionFromEnvelopeQuota(envelopeQuotaRaw) {
  if (!envelopeQuotaRaw || typeof envelopeQuotaRaw !== 'object') return null;

  const plan = envelopeQuotaRaw.plan && typeof envelopeQuotaRaw.plan === 'object'
    ? envelopeQuotaRaw.plan
    : {};
  const currentPeriod = envelopeQuotaRaw.current_period ?? envelopeQuotaRaw.currentPeriod ?? null;

  return {
    planId: plan.id ?? null,
    planName: plan.name ?? plan.title ?? null,
    planCode: plan.code ?? plan.slug ?? null,
    organizationId: null,
    subscriptionStatus: null,
    currentPeriod,
    usage: normalizeUsage(envelopeQuotaRaw, { plan }),
    envelopeQuota: normalizeEnvelopeQuotaMeta(envelopeQuotaRaw),
    raw: { envelope_quota: envelopeQuotaRaw },
  };
}

export function applyEnvelopeQuotaToSubscription(existing, envelopeQuotaRaw) {
  const fromQuota = subscriptionFromEnvelopeQuota(envelopeQuotaRaw);
  if (!fromQuota) return existing;
  if (!existing) return fromQuota;

  const creditsFromQuota = normalizeEnvelopeCredits(
    envelopeQuotaRaw.envelope_credits ?? envelopeQuotaRaw.envelopeCredits
  );

  return {
    ...existing,
    planName: fromQuota.planName ?? existing.planName,
    planCode: fromQuota.planCode ?? existing.planCode,
    currentPeriod: fromQuota.currentPeriod ?? existing.currentPeriod,
    envelopeCredits: creditsFromQuota ?? existing.envelopeCredits ?? null,
    usage: {
      ...existing.usage,
      envelopes: fromQuota.usage?.envelopes ?? existing.usage?.envelopes,
      users: existing.usage?.users,
    },
    envelopeQuota: fromQuota.envelopeQuota,
    raw: {
      ...existing.raw,
      envelope_quota: envelopeQuotaRaw,
    },
  };
}

function buildEnvelopeQuotaMetaFromSubscription(raw, envelopeQuotaBlock = null) {
  const block = envelopeQuotaBlock && typeof envelopeQuotaBlock === 'object'
    ? envelopeQuotaBlock
    : {};
  return normalizeEnvelopeQuotaMeta({
    ...block,
    metered: raw.metered ?? block.metered,
    bypass: raw.bypass ?? block.bypass,
    envelopes_sent_per_month:
      raw.usage?.envelopes_sent_per_month
      ?? block.envelopes_sent_per_month,
    pay_per_envelope: block.pay_per_envelope ?? raw.pay_per_envelope,
  });
}

export function normalizeSubscription(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const envelopeQuotaBlock = extractEnvelopeQuotaFromPayload(raw);
  const envelopeQuota = buildEnvelopeQuotaMetaFromSubscription(raw, envelopeQuotaBlock);

  const envelopeCredits = normalizeEnvelopeCredits(
    raw.envelope_credits
    ?? raw.envelopeCredits
    ?? envelopeQuotaBlock?.envelope_credits
    ?? envelopeQuotaBlock?.envelopeCredits
  );

  if (envelopeQuotaBlock) {
    const fromQuota = subscriptionFromEnvelopeQuota(envelopeQuotaBlock);
    if (fromQuota) {
      return {
        ...fromQuota,
        organizationId: normalizeOrganizationIdFromSubscription(raw),
        organizationTier: raw.organization_tier ?? raw.organizationTier ?? null,
        organizationStatus: raw.organization_status ?? raw.organizationStatus ?? null,
        metered: raw.metered === true,
        bypass: raw.bypass === true,
        billingAnchorAt: raw.billing_anchor_at ?? raw.billingAnchorAt ?? null,
        subscriptionStatus: raw.subscription_status ?? raw.status ?? null,
        message: raw.message ?? null,
        envelopeCredits,
        billingProvider: raw.billing_provider ?? raw.billingProvider ?? null,
        usage: {
          ...fromQuota.usage,
          ...normalizeUsage(raw.usage, raw),
          envelopes:
            fromQuota.usage?.envelopes
            ?? normalizeUsage(raw.usage, raw).envelopes,
        },
        envelopeQuota,
        raw,
      };
    }
  }

  const plan = raw.plan && typeof raw.plan === 'object' ? raw.plan : {};
  const currentPeriod = raw.current_period ?? raw.currentPeriod ?? null;

  return {
    planId: plan.id ?? raw.plan_id ?? null,
    planName: plan.name ?? plan.title ?? null,
    planCode: plan.code ?? plan.slug ?? null,
    organizationId: normalizeOrganizationIdFromSubscription(raw),
    organizationTier: raw.organization_tier ?? raw.organizationTier ?? null,
    organizationStatus: raw.organization_status ?? raw.organizationStatus ?? null,
    metered: raw.metered === true,
    bypass: raw.bypass === true,
    billingAnchorAt: raw.billing_anchor_at ?? raw.billingAnchorAt ?? null,
    subscriptionStatus: raw.subscription_status ?? raw.status ?? null,
    message: raw.message ?? null,
    envelopeCredits,
    billingProvider: raw.billing_provider ?? raw.billingProvider ?? null,
    currentPeriod,
    usage: normalizeUsage(raw.usage, raw),
    envelopeQuota,
    raw,
  };
}

function normalizeOrganizationIdFromSubscription(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.organization_id ?? raw.organizationId ?? raw.org_id;
  if (id === undefined || id === null || id === '') return null;
  return String(id);
}

export function getOrganizationIdFromSubscription(subscription) {
  if (!subscription) return null;
  return subscription.organizationId ?? null;
}

export function extractSubscriptionFromUserPayload(data) {
  if (!data || typeof data !== 'object') return null;
  const nested = data.data && typeof data.data === 'object' ? data.data : null;
  const user = data.user || nested?.user || null;

  const envelopeQuota =
    extractEnvelopeQuotaFromPayload(data)
    ?? extractEnvelopeQuotaFromPayload(nested);

  const raw =
    data.subscription
    ?? nested?.subscription
    ?? user?.subscription
    ?? null;

  let subscription = normalizeSubscription(raw);
  if (envelopeQuota) {
    subscription = applyEnvelopeQuotaToSubscription(subscription, envelopeQuota);
  }
  if (!subscription) return null;

  if (subscription.organizationId) return subscription;

  const orgId =
    user?.primary_organization_id
    ?? user?.organization_id
    ?? user?.organizationId
    ?? user?.default_organization_id
    ?? data.organization_id
    ?? nested?.organization_id;
  if (orgId == null || orgId === '') return subscription;

  return { ...subscription, organizationId: String(orgId) };
}

/**
 * Short label for header / compact UI (remaining sends this period).
 * @returns {string|null} null when usage data is unavailable
 */
export function formatEnvelopeRemaining(usage, subscription = null) {
  const entry = usage?.envelopes;
  if (!entry) return null;
  const { used, limit, remaining } = entry;
  if (limit == null) return 'Unlimited envelopes';
  const left =
    remaining != null && Number.isFinite(remaining)
      ? Math.max(0, remaining)
      : Math.max(0, limit - used);
  const base = `${left} envelope${left === 1 ? '' : 's'} remaining`;
  const credits = getEnvelopeCreditsRemaining(subscription);
  if (credits != null && credits > 0) {
    return `${base} · ${formatEnvelopeCreditsLabel(credits)}`;
  }
  return base;
}

export function formatUsageLine(usage, key = 'envelopes') {
  const entry = usage?.[key];
  if (!entry) return null;
  const { used, limit } = entry;
  const labels = USAGE_RESOURCE_LABELS[key] || {
    unit: key,
    units: `${key}s`,
    suffix: '',
  };
  if (limit == null) {
    return `${used} ${used === 1 ? labels.unit : labels.units}${labels.suffix} (∞)`;
  }
  const unit = limit === 1 ? labels.unit : labels.units;
  return `${used} of ${limit} ${unit}${labels.suffix}`;
}

/** Contract copy: `{used} of {limit ?? '∞'} envelopes sent · Resets {date}` */
export function formatEnvelopeUsageSummary(subscription, formatDateFn) {
  if (!shouldShowSubscriptionUsage(subscription)) return null;
  const line = formatUsageLine(subscription.usage, 'envelopes');
  if (!line) return null;
  const resetsAt =
    subscription.currentPeriod?.resets_at
    ?? subscription.currentPeriod?.resetsAt
    ?? null;
  if (!resetsAt || typeof formatDateFn !== 'function') return line;
  const formatted = formatDateFn(resetsAt);
  if (!formatted || formatted === 'N/A' || formatted === '—') return line;
  return `${line} · Resets ${formatted}`;
}

export function isUsageNearLimit(usage, key = 'envelopes', threshold = 0.8) {
  const entry = usage?.[key];
  if (!entry || entry.limit == null) return false;
  if (entry.limit <= 0) return false;
  return entry.used / entry.limit >= threshold;
}

function resolveCanSendFromSubscription(subscription) {
  if (!subscription) return null;
  if (subscription.bypass === true || subscription.envelopeQuota?.bypass === true) {
    return true;
  }
  const metered = subscription.metered ?? subscription.envelopeQuota?.metered;
  if (metered === false) return true;
  if (subscription.envelopeQuota?.canSend === false) return false;
  if (subscription.envelopeQuota?.canSend === true) return true;

  const entry = subscription.usage?.envelopes;
  if (entry?.canSend === false) return false;
  if (entry?.canSend === true) return true;

  const monthly = subscription.raw?.envelope_quota?.envelopes_sent_per_month
    ?? subscription.raw?.usage?.envelopes_sent_per_month;
  if (monthly?.can_send === false) return false;
  if (monthly?.can_send === true) return true;

  return null;
}

/** True when subscription / envelope_quota shows sending is blocked (client-side guard before send). */
export function isEnvelopeSendQuotaExceeded(subscription) {
  if (!subscription) return false;
  if (subscription.bypass === true || subscription.envelopeQuota?.bypass === true) {
    return false;
  }
  const metered = subscription.metered ?? subscription.envelopeQuota?.metered;
  if (metered === false) return false;

  const canSend = resolveCanSendFromSubscription(subscription);
  if (canSend === false) return true;
  if (canSend === true) return false;

  const entry = subscription?.usage?.envelopes;
  if (!entry || entry.limit == null) return false;
  if (entry.remaining != null && Number.isFinite(entry.remaining)) {
    return entry.remaining <= 0;
  }
  return entry.used >= entry.limit;
}

export function getEnvelopeQuotaExceededMessage(subscription) {
  const entry = subscription?.usage?.envelopes;
  if (entry?.limit != null) {
    const limit = entry.limit;
    const used = entry.used ?? 0;
    if (used >= limit) {
      return `You have used all ${limit} envelope${limit === 1 ? '' : 's'} for this billing period (${used} of ${limit}). Upgrade your plan to send more.`;
    }
  }
  return 'You have reached your plan limit for this billing period. Upgrade your plan to send more envelopes.';
}

export function isQuotaExceededError(error) {
  if (error?.response?.status !== 403) return false;
  const data = error.response?.data || {};
  const code = data.code ?? data.error;
  return code === 'quota_exceeded' || String(data.message || '').toLowerCase().includes('quota');
}

export function isRevokedEnvelopeBlockedError(error) {
  if (error?.response?.status !== 422) return false;
  const data = error.response?.data || {};
  const code = data.code ?? data.error;
  const msg = String(data.message || '').toLowerCase();
  return (
    code === 'revoked_envelope_not_resendable' ||
    code === 'envelope_revoked' ||
    code === 'revoked' ||
    msg.includes('revoked')
  );
}

export function getRevokedEnvelopeCopyEndpoint(error) {
  const data = error?.response?.data || {};
  return data.copy_endpoint || data.copyEndpoint || null;
}

export function getQuotaExceededMessage(error) {
  const data = error?.response?.data || {};
  return data.message || 'You have reached your plan limit. Upgrade to send more envelopes.';
}

export function getPayPerEnvelopeFromError(error) {
  const data = error?.response?.data || {};
  const raw =
    data.pay_per_envelope
    ?? data.envelope_quota?.pay_per_envelope
    ?? null;
  return normalizePayPerEnvelope(
    raw ? { pay_per_envelope: raw } : null
  );
}

/** Map 403 `quota` / `envelope_quota` into a syncable envelope_quota-shaped object. */
export function syncSubscriptionFromQuotaError(error) {
  const data = error?.response?.data;
  if (!data) return null;
  const envelopeQuota =
    extractEnvelopeQuotaFromPayload(data)
    ?? (data.envelope_quota ? data.envelope_quota : null);
  if (envelopeQuota) return envelopeQuota;

  const quota = data.quota;
  if (!quota || typeof quota !== 'object') return null;

  const remainingNum = Number(quota.remaining);
  const canSend =
    Number.isFinite(remainingNum)
      ? remainingNum > 0
      : false;

  return {
    metered: true,
    bypass: false,
    envelopes_sent_per_month: {
      used: quota.used,
      limit: quota.limit,
      remaining: quota.remaining,
      can_send: canSend,
    },
    current_period: {
      start: quota.period_start,
      end: quota.period_end,
      resets_at: quota.resets_at,
    },
  };
}
